import React, { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';

export interface PreviewTheme {
  themeTitle: string;
  userThemeId: string;
  isDefault?: boolean;
}

interface PreviewContextType {
  currentSection: string;
  setCurrentSection: (section: string) => void;
  previewTheme: PreviewTheme | null;
  setPreviewTheme: (theme: PreviewTheme | null) => void;
  suppressPreviewResetRef: React.MutableRefObject<boolean>;
  clearPreviewAfterActivation: () => void;
  themeActivatedTrigger: number;
  lastActivatedUserThemeId: string | null;
  triggerThemeActivated: (userThemeId: string) => void;
  openPanelTrigger: number;
  triggerOpenPanel: () => void;
  isPreviewOpen: boolean;
  setIsPreviewOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const PreviewContext = createContext<PreviewContextType | null>(null);

interface PreviewProviderProps {
  children: ReactNode;
}

export const PreviewProvider: React.FC<PreviewProviderProps> = ({ children }) => {
  const [currentSection, setCurrentSection] = useState<string>('dashboard');
  const [previewTheme, setPreviewTheme] = useState<PreviewTheme | null>(null);
  const suppressPreviewResetRef = useRef(false);
  const clearPreviewAfterActivation = useCallback(() => {
    suppressPreviewResetRef.current = true;
    setPreviewTheme(null);
  }, []);
  const [themeActivatedTrigger, setThemeActivatedTrigger] = useState<number>(0);
  const [lastActivatedUserThemeId, setLastActivatedUserThemeId] = useState<string | null>(null);
  const [openPanelTrigger, setOpenPanelTrigger] = useState<number>(0);
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(() => {
    const saved = localStorage.getItem('previewPanelOpen');
    if (saved !== null) {
      try { return JSON.parse(saved); } catch { return true; }
    }
    return true;
  });

  const triggerThemeActivated = useCallback((userThemeId: string) => {
    setLastActivatedUserThemeId(userThemeId);
    setThemeActivatedTrigger(prev => prev + 1);
  }, []);

  const triggerOpenPanel = useCallback(() => {
    setOpenPanelTrigger(prev => prev + 1);
  }, []);

  const value: PreviewContextType = {
    currentSection,
    setCurrentSection,
    previewTheme,
    setPreviewTheme,
    suppressPreviewResetRef,
    clearPreviewAfterActivation,
    themeActivatedTrigger,
    lastActivatedUserThemeId,
    triggerThemeActivated,
    openPanelTrigger,
    triggerOpenPanel,
    isPreviewOpen,
    setIsPreviewOpen,
  };

  return (
    <PreviewContext.Provider value={value}>
      {children}
    </PreviewContext.Provider>
  );
};

export const usePreview = (): PreviewContextType => {
  const context = useContext(PreviewContext);
  if (!context) {
    throw new Error('usePreview must be used within a PreviewProvider');
  }
  return context;
};

export default PreviewContext;
